import { call, put, select } from 'redux-saga/effects';
import _ from 'underscore';

import { ToasterService } from '~commonInfra/services/ToasterService';
import { Location } from '~commonInfra/utils/Location';
import { Set } from '~commonInfra/utils/Set';
import { requestFailed } from '~src/bootstrap/redux/actions/RequestFailedAction';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { Messages } from '~src/codegen/Localize';
import { LocationTypes } from '~src/personalSpace/model/ILocationInfo';
import { selectContainerInfo } from '~src/personalSpace/selectors/ContainerInfoSelector';
import { ContainerInfoState } from '~src/personalSpace/state/ContainerInfoState';
import { isIProjectBase } from '~src/projects/models/IProjectInfo';
import {
    removeFromContentSelection
} from '~src/spacesPlatform/actions/ContentExplorerSelectionActions';
import {
    DeleteContentAction, deleteContentSucceeded
} from '~src/spacesPlatform/actions/DeleteContentAction';
import {
    ContentTypeAndID, DeleteContentsAction, IDeleteContentsResult
} from '~src/spacesPlatform/actions/DeleteContentsAction';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ExploreContentApi_ContentType } from '~src/spacesPlatform/models/IContent';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { isIProjectItemInfo, ISpaceTypeInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';
import { IViewInfo } from '~src/views/models/IViewInfo';

import { replaceDatasourceIdWithConnectionIdForMixedContent } from '../SpacesSagaHelpers';

export function openConfirmationDialog(contents: IContentInfo[], spaceInfo: SpaceInfo) {
  return DeleteContentsAction.confirmDeleteContents(contents, spaceInfo);
}

export function* deleteContents(action: DeleteContentAction) {
  try {
    let mutatableContentCopy: IContentInfo[] = [];
    mutatableContentCopy = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, action.contentArray);

    const currentSpaceInfo: SpaceInfo = yield select(selectSpaceInfo);

    let result: IDeleteContentsResult = undefined;
    try {
       result = yield call(openConfirmationDialog, mutatableContentCopy, currentSpaceInfo);
    } catch(e) {
      return; // Catches the rejected dismiss-dialog promise
    }

    yield put(removeFromContentSelection(...action.contentArray));

    // if we've deleted the space whose page we are currently viewing
    // navigate back to the parent project
    const isCurrentSpace = (item: IContentInfo) => {
      return (item.contentType === currentSpaceInfo.spaceType
            && item.id === currentSpaceInfo.item.id);
    };

    let workbookForSpaceAlsoDeleted: boolean =
      allViewsInWorkbookDeletedForCurrentWorkbookSpace(action.contentArray, result, currentSpaceInfo);

    const deletedItemIfInCurrentSpace: IContentInfo = _.find(action.contentArray, isCurrentSpace);

    if (!!deletedItemIfInCurrentSpace || !!workbookForSpaceAlsoDeleted) {
      const parentProjectId: string = workbookForSpaceAlsoDeleted?
        getParentProjectId(currentSpaceInfo.item) :
        getParentProjectId(deletedItemIfInCurrentSpace);

      const urls: SpacesSiteUrls = yield select(siteUrlsSelector);

      // keep these success toasts when navigating away to make sure the user knows what happened
      const toaster: ToasterService = ToasterService.instance;
      toaster.markToastsAsDoNotDismissOnLocationChange();

      const containerInfo: ContainerInfoState = yield select(selectContainerInfo);

      if (parentProjectId) {
        Location.instance.setUrl(urls.projectUrl(parentProjectId));
      } else if(typeof deletedItemIfInCurrentSpace !== 'undefined' && deletedItemIfInCurrentSpace.contentType === 'collection') {
        Location.instance.setUrl(urls.collectionsUrl());
      } else if (containerInfo.type === LocationTypes.PersonalSpace) {
        Location.instance.setUrl(urls.personalSpaceUrl());
      } else {
        Location.instance.setUrl(urls.exploreUrl());
      }
    } else {
      // refetch content for this space.
      yield put(requestSpaceContents());
      yield put(deleteContentSucceeded());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.deleteFailed()));
  }
}

// exported for testing
export function allViewsInWorkbookDeletedForCurrentWorkbookSpace(
  contentArray: IContentInfo[],
  result: IDeleteContentsResult,
  currentSpaceInfo: SpaceInfo) {

  const isInWorkbookViewsSubSpace = (currentSpaceInfo.spaceType === 'workbook' && currentSpaceInfo.subspaceName === 'views');
  if(!isInWorkbookViewsSubSpace) {
    return false;
  }

  if (contentArray && contentArray.length > 0) {

    const deletedViews = contentArray.filter((item: IContentInfo<ExploreContentApi_ContentType>) => {
      return item.contentType === 'view' && result.deletedItems && arrayContainsViewWithId(result.deletedItems, item.id);
    });
    const workbooksDeleted = DeleteContentsAction.determineWorkbooksToBeDeleted((Set.fromIdItemArray(deletedViews) as Set<IViewInfo>));
    return workbooksDeleted.length === 1 && workbooksDeleted[0].id === currentSpaceInfo.item.id;
  }
  return false;
}


// exported
export function arrayContainsViewWithId(arr: ContentTypeAndID[], idToMatch: string): boolean {
  return arr.findIndex((item: ContentTypeAndID) => item.contentType === 'view' && item.id  === idToMatch) >= 0;
}

function getParentProjectId(item: IContentInfo | ISpaceTypeInfo) {
  if (item.contentType === 'project' && isIProjectBase(item)) {
    if (!item.topLevelProject) {
      return item.parentId;
    }
  }
  else if (isIProjectItemInfo(item) && item.project) {
    return item.project.id;
  }

  return undefined;
}
